import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  CounterType,
  formatDateToMMDDYYYY,
  parseMMDDYYYYToDate,
  RoutineType,
  useCreateMutation,
  useUpdateIssue,
  useWorkspaceLabels,
  WorkflowQuestionType,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Platform,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { mixed, string, object as yupObject } from 'yup';
import { useAutoFocusRef } from '../../hooks';
import { Spacing } from '../../styles';
import { Colors } from '../../themes/colors';
import { ControlledTextInput, SaveFormTop } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { TopModalBar } from '../modals/topModalBar';
import { AdjustCreationDateField } from './adjustCreationDateField';
import { CreatedDateField } from './createdDateField';
import { LabelSelector } from './labelSelector';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type IssueEditModalProps = {
  visible: boolean;
  hide: () => void;
  issueId: string;
  currentTitle: string;
  workspaceId?: string;
  currentLabels?: Array<{ id: string; name: string }>;
  currentAdjustCreationDate?: string;
  currentCreatedAt?: string;
  issue: any; // Add issue prop for routine functionality
  onRoutineEdition: () => void;
};

export const IssueEditModal: React.FC<IssueEditModalProps> = (
  props: IssueEditModalProps
) => {
  const { t } = useTranslation();
  const isDesktop = () => Platform.OS === 'web';

  const { mutateAsync: updateIssue } = useUpdateIssue();
  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  // Fetch labels from current workspace
  const { labels: availableLabels } = useWorkspaceLabels(props.workspaceId);

  const formSchema = yupObject({
    title: string().required(),
    adjustCreationDate: mixed().optional(),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      title: props.currentTitle ?? '',
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
    },
  });

  const onSubmit = async (data: any) => {
    const input = {
      id: props.issueId,
      title: data.title,
      labels: selectedLabels.map((l) => ({ id: l.id })),
      adjustCreationDate: data.adjustCreationDate
        ? parseMMDDYYYYToDate(String(data.adjustCreationDate))
        : undefined,
    };

    await updateIssue({ input: input });
    props.hide();
  };

  const onCancel = () => {
    reset({
      title: props.currentTitle,
      adjustCreationDate: props.currentAdjustCreationDate
        ? formatDateToMMDDYYYY(props.currentAdjustCreationDate)
        : undefined,
    });
    setSelectedLabels(props.currentLabels || []);
    setLabelsChanged(false);
    props.hide();
  };

  const elementFocusCallBack = useAutoFocusRef();
  const [selectedLabels, setSelectedLabels] = React.useState<
    Array<{ id: string; name: string }>
  >(props.currentLabels || []);
  const [labelsChanged, setLabelsChanged] = React.useState(false);

  React.useEffect(() => {
    if (props.visible) {
      setSelectedLabels(props.currentLabels || []);
      setLabelsChanged(false);
    }
  }, [props.visible, props.currentLabels]);

  const handleLabelsChange = (
    newLabels: Array<{ id: string; name: string }>
  ) => {
    setSelectedLabels(newLabels);

    // Check if labels have actually changed
    const currentLabelIds = (props.currentLabels || []).map((l) => l.id).sort();
    const newLabelIds = newLabels.map((l) => l.id).sort();
    const hasChanged =
      currentLabelIds.length !== newLabelIds.length ||
      currentLabelIds.some((id, index) => id !== newLabelIds[index]);

    setLabelsChanged(hasChanged);
  };

  const handleCreateLabel = async (name: string) => {
    const result = await createLabel({
      input: { name, workspace: { id: props.workspaceId } },
    });
    return result;
  };

  // Render Revise Submission section
  const renderReviseSubmissionSection = () => {
    const isTallyCounter =
      props.issue.routineVersion?.type === RoutineType.Counter &&
      props.issue.routineVersion?.config?.counterType === CounterType.Tally;

    const showEditRoutine =
      props.issue.type === WorkflowQuestionType.Routine && !isTallyCounter;

    if (showEditRoutine) {
      return (
        <View style={styles.formItem}>
          <Text style={styles.formItemTitle}>
            {t('issue.editModal.reviseSubmission')}
          </Text>
          <TouchableOpacity
            style={styles.reviseSubmissionItem}
            onPress={props.onRoutineEdition}
            activeOpacity={0.7}
          >
            <View style={styles.reviseSubmissionContent}>
              <View style={styles.reviseSubmissionText}>
                <Text style={styles.reviseSubmissionTitle}>
                  {props.issue?.routineVersion?.config.title}
                </Text>
              </View>
              <MaterialCommunityIcons
                name="chevron-right"
                size={20}
                color={Colors.white}
              />
            </View>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  return (
    <Modal
      visible={props.visible}
      onRequestClose={props.hide}
      animationType={isDesktop() ? 'none' : 'slide'}
      transparent={isDesktop()}
      presentationStyle={isDesktop() ? 'overFullScreen' : 'pageSheet'}
    >
      <View style={isDesktop() && styles.desktopWrapper}>
        <View
          style={isDesktop() ? styles.desktopContainer : styles.mobileContainer}
        >
          <SafeAreaView style={{ flex: 1 }} edges={['top']}>
            <SaveFormTop
              isDirty={isDirty || labelsChanged}
              isValid={isValid}
              onSubmit={handleSubmit(onSubmit)}
              title={t('issue.editModal.header')}
              saveLabel={t('issue.editModal.save')}
              topBar={TopModalBar}
              onCancel={onCancel}
            >
              {Platform.OS === 'web' ? (
                <View style={styles.formContainer}>
                  {renderReviseSubmissionSection()}
                  <View style={styles.formItem}>
                    <View>
                      <Text style={styles.formItemTitle}>
                        {t('issue.new.title')}
                      </Text>
                    </View>
                    <ControlledTextInput
                      control={control}
                      name="title"
                      height={44}
                      placeholder={t('issue.new.optionalPlaceholder')}
                      disableInnerStyle={false}
                      outlinedTextInput
                      innerRef={elementFocusCallBack}
                    />
                  </View>
                  {!!props.workspaceId && (
                    <View style={styles.formItem}>
                      <LabelSelector
                        selectedLabels={selectedLabels}
                        availableLabels={availableLabels}
                        onLabelsChange={handleLabelsChange}
                        onCreateLabel={handleCreateLabel}
                      />
                    </View>
                  )}
                  <View style={styles.formItem}>
                    <AdjustCreationDateField
                      control={control}
                      setValue={setValue}
                    />
                  </View>
                  <View style={styles.formItem}>
                    <CreatedDateField
                      createdAt={props.currentCreatedAt || ''}
                      adjustCreationDate={props.currentAdjustCreationDate}
                    />
                  </View>
                </View>
              ) : (
                <KeyboardSafeView style={styles.keyboardContainer}>
                  <ScrollView
                    contentContainerStyle={styles.formContainer}
                    style={{ borderRadius: 12 }}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    scrollEnabled={true}
                    automaticallyAdjustKeyboardInsets={true}
                    keyboardDismissMode="interactive"
                  >
                    {renderReviseSubmissionSection()}
                    <View style={styles.formItem}>
                      <View>
                        <Text style={styles.formItemTitle}>
                          {t('issue.new.title')}
                        </Text>
                      </View>
                      <ControlledTextInput
                        control={control}
                        name="title"
                        height={44}
                        placeholder={t('issue.new.optionalPlaceholder')}
                        disableInnerStyle={false}
                        outlinedTextInput
                        innerRef={elementFocusCallBack}
                      />
                    </View>
                    {!!props.workspaceId && (
                      <View style={styles.formItem}>
                        <LabelSelector
                          selectedLabels={selectedLabels}
                          availableLabels={availableLabels}
                          onLabelsChange={handleLabelsChange}
                          onCreateLabel={handleCreateLabel}
                        />
                      </View>
                    )}
                    <View style={styles.formItem}>
                      <AdjustCreationDateField
                        control={control}
                        setValue={setValue}
                      />
                    </View>
                    <View style={styles.formItem}>
                      <CreatedDateField
                        createdAt={props.currentCreatedAt || ''}
                        adjustCreationDate={props.currentAdjustCreationDate}
                      />
                    </View>
                  </ScrollView>
                </KeyboardSafeView>
              )}
            </SaveFormTop>
          </SafeAreaView>
        </View>
      </View>
    </Modal>
  );
};

const styles = themedStyleSheet((role) => ({
  formContainer: {
    gap: 8,
    paddingBottom: Platform.OS === 'web' ? 100 : 400,
    paddingTop: 16,
    flexGrow: 1,
  },
  keyboardContainer: {
    flex: 1,
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 16,
    color: role.textStrong,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    backgroundColor: role.surface,
    marginBottom: 4,
  },
  desktopWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: role.inverseDim,
  },
  desktopContainer: {
    backgroundColor: role.surfaceHover,
    width: 560,
    minHeight: 350,
    borderRadius: 12,
  },
  mobileContainer: {
    backgroundColor: role.surfaceHover,
    height: '100%',
  },
  reviseSubmissionItem: {
    backgroundColor: Colors.blue,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: role.borderControl,
  },
  reviseSubmissionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  reviseSubmissionIcon: {
    marginRight: 12,
  },
  reviseSubmissionText: {
    flex: 1,
  },
  reviseSubmissionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.white,
  },
}));

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) { id name }
  }
`;
